import {useCallback, useEffect, useState} from 'react'
import {Banner} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {parseRepositoryCommand} from '@github-ui/one-copilot-composer/existing'

import type {APITask} from '../types/api-task'

/**
 * Whether a session for `task` has a repository checked out: a GitHub repository
 * (a repository-less task carries id `0`) or an Azure DevOps one.
 */
export function taskHasRepository(task: Pick<APITask, 'repository' | 'azure_devops_repository_context'>): boolean {
  return (task.repository?.id ?? 0) > 0 || task.azure_devops_repository_context !== undefined
}

export function repositoryCommandRefusal(command: string): string {
  return `${command} needs a repository, and this session doesn't have one. Start a session from a repository to use it.`
}

interface RepositoryCommandRefusal {
  message: string
  /** The task the command was refused for; the refusal is shown only while it is active. */
  taskId: string | undefined
}

function RepositoryCommandFeedback({refusal, onDismiss}: {refusal: RepositoryCommandRefusal; onDismiss: () => void}) {
  // `Banner` carries no live region, so the refusal of a command that never
  // becomes a turn would otherwise reach a screen reader only by chance.
  useEffect(() => {
    announce(refusal.message)
  }, [refusal])

  return (
    <Banner variant="info" title="Repository required" hideTitle description={refusal.message} onDismiss={onDismiss} />
  )
}

/**
 * Refuses a repository-dependent slash command (`/review`, `/security-review`,
 * `/init`, `/blame`) when the session has no repository, and returns the
 * dismissible banner that explains why for the host to render near its composer.
 *
 * Without a checkout the runtime would still run the command, sending an agent
 * to search for code that is not there. `guardRepositoryCommand` returns whether
 * the text was refused; a `true` result means the composer must not dispatch it.
 * When the repository cannot be determined (`undefined`, or the lookup throws)
 * the command is left to the host, so a failed lookup never blocks a command
 * that would have worked.
 */
export function useRepositoryCommandGuard({
  resolveHasRepository,
  taskId,
  onDismissFeedback,
}: {
  resolveHasRepository: () => boolean | undefined | Promise<boolean | undefined>
  taskId: string | undefined
  onDismissFeedback?: () => void
}) {
  const [refusal, setRefusal] = useState<RepositoryCommandRefusal>()
  const dismissFeedback = useCallback(() => {
    setRefusal(undefined)
    onDismissFeedback?.()
  }, [onDismissFeedback])

  const guardRepositoryCommand = useCallback(
    async (text: string) => {
      const command = parseRepositoryCommand(text)
      if (!command) return false

      let hasRepository: boolean | undefined
      try {
        hasRepository = await resolveHasRepository()
      } catch {
        return false
      }
      if (hasRepository !== false) return false

      setRefusal({message: repositoryCommandRefusal(command), taskId})
      return true
    },
    [resolveHasRepository, taskId],
  )

  const currentRefusal = refusal?.taskId === taskId ? refusal : undefined
  return {
    guardRepositoryCommand,
    feedback: currentRefusal ? (
      <RepositoryCommandFeedback refusal={currentRefusal} onDismiss={dismissFeedback} />
    ) : null,
  }
}
